import { Stack } from "@mui/material"
import { Context, ReactElement, useContext } from "react"
import { useForm } from "react-hook-form"

import { useFilterReferenceMappings } from "../../../../../hooks/useFilterReferenceMappings"
import { GRID_SPACING } from "../../../../../themes/defaultThemeParams"
import FormErrors from "../../../../formErrors/formErrors"
import FormHeader from "../../../../formHeader/formHeader"
import FormNavigation from "../../../../formNavigation/formNavigation"
import ValidatedAutocomplete from "../../../../inputs/validated/validatedSelect"
import { AppCard } from "../../../../surfaces/appCard"
import { MANU_SUBTITLE } from "../../../common/labels"
import { IDeviceFormContextValue } from "../../../types/providerTypes"
import {
    HP_MANUFACTURER_REG,
    hpDeviceFormLabelMapping,
} from "../../formSpecs/hpSpec"
import { HpDeviceFormData } from "../../types/providerTypes"

const mapRegisterNameToValues = (
    formData: HpDeviceFormData
): { [name: string]: NonNullable<unknown> | undefined | null } => ({
    [HP_MANUFACTURER_REG]: formData.manufacturerId,
})

interface IEbManufacturerProps {
    context: Context<IDeviceFormContextValue<HpDeviceFormData>>
}

/** Allows user to select an Electric Boiler manufacturer */
const EbManufacturer = ({ context }: IEbManufacturerProps): ReactElement => {
    const { currentIndex, setCurrentIndex, formData, setFormData } =
        useContext(context)

    const {
        control,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: mapRegisterNameToValues(formData),
        values: mapRegisterNameToValues(formData),
        shouldUnregister: true,
    })

    const { manufacturerMappings, manufacturerIds, isLoading } =
        useFilterReferenceMappings()

    const updateManufacturer = (): void => {
        formData.manufacturerId = watch(HP_MANUFACTURER_REG) as string
        setFormData({ ...formData })
    }

    const onClickNext = (): void => {
        updateManufacturer()
        setCurrentIndex(currentIndex + 1)
    }

    return (
        <AppCard>
            <Stack spacing={GRID_SPACING * 2}>
                <FormHeader
                    header="Manufacturer"
                    subHeader={MANU_SUBTITLE}
                    index={currentIndex + 1}
                />
                <ValidatedAutocomplete
                    control={control}
                    required
                    registerName={HP_MANUFACTURER_REG}
                    // TPDP
                    label={hpDeviceFormLabelMapping[HP_MANUFACTURER_REG]}
                    options={manufacturerIds}
                    getOptionLabel={(option) => manufacturerMappings[option]}
                    loading={isLoading}
                />
                <FormErrors
                    errors={errors}
                    getFriendlyName={(key: string) =>
                        hpDeviceFormLabelMapping[key]
                    }
                />
                <FormNavigation
                    onClickNext={() => handleSubmit(onClickNext)()}
                    nextText="Next"
                    nextDisabled={isLoading}
                />
            </Stack>
        </AppCard>
    )
}

export default EbManufacturer
